import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useZabava } from './zabavaTok';
import { getToken } from '../../api/http';
import { pustiEfekat, zvukKontekst } from './zvuci';

/* Efekti "zabave" kod operatera (pali ih admin, stižu uživo):
   - ZabavaTraka: tekst koji ide sleva nadesno kroz zaglavlje
   - ZabavaEfekti: slika koja leti i rotira po ekranu (ne može se zatvoriti), zvuk u pozadini, animacije.
   Granice: bez treptanja/bljeskanja; slika i animacije ne hvataju klikove (može da se radi); jačina zvuka do 70%;
   uz sistemsko "smanji pokret" animacije su ređe i sporije. */

const smanjiPokret = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** traka sa tekstom u zaglavlju */
export function ZabavaTraka() {
    const z = useZabava();
    if (!z.aktivno || !z.traka || !z.traka.ukljuceno || !z.traka.tekst) return null;
    return (
        <div className={`zabava-traka boja-${z.traka.boja}`} aria-live="off">
            <span style={{ animationDuration: `${z.traka.brzina}s` }}>{z.traka.tekst}</span>
        </div>
    );
}

/** slika koja leti po ekranu (odbija se od ivica) i rotira */
function LeteciSlika({ slika }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const v = slika.velicina;
        let x = Math.random() * (window.innerWidth - v), y = Math.random() * (window.innerHeight - v);
        const brzina = slika.brzina * (smanjiPokret() ? 0.4 : 1);
        let dx = brzina * (Math.random() < 0.5 ? -1 : 1), dy = brzina * 0.8 * (Math.random() < 0.5 ? -1 : 1);
        let ugao = 0, id;
        const korak = () => {
            const w = window.innerWidth - v, h = window.innerHeight - v;
            x += dx; y += dy;
            if (x <= 0 || x >= w) { dx = -dx; x = Math.max(0, Math.min(w, x)); }
            if (y <= 0 || y >= h) { dy = -dy; y = Math.max(0, Math.min(h, y)); }
            if (slika.rotacija) ugao = (ugao + brzina * 0.8) % 360;
            el.style.transform = `translate(${x}px, ${y}px) rotate(${ugao}deg)`;
            id = requestAnimationFrame(korak);
        };
        id = requestAnimationFrame(korak);
        return () => cancelAnimationFrame(id);
    }, [slika.velicina, slika.brzina, slika.rotacija]);
    return (
        <span ref={ref} className="zabava-slika" style={{ width: slika.velicina, height: slika.velicina }} aria-hidden="true">
            <SlikaOkvir slika={slika} />
        </span>
    );
}

/** slika u okviru: original, krug ili srce (maska — i GIF ostaje animiran) */
export function SlikaOkvir({ slika }) {
    /* data = upravo izabrana (još nesačuvana) slika u admin pregledu; sačuvana se čita sa servera po verziji */
    const src = slika.izvor !== 'data' ? '/images/logo/tacho-logo.png'
        : slika.data || `/API/podesavanja/zabava/slika?v=${slika.verzija}&t=${encodeURIComponent(getToken() || '')}`;
    return <span className={`zabava-okvir oblik-${slika.oblik || 'original'}`}><img src={src} alt="" /></span>;
}

/** padajuće čestice na platnu (konfete, sneg, emodži, mehurići) */
function Animacija({ a }) {
    const ref = useRef(null);
    useEffect(() => {
        const c = ref.current;
        if (!c) return undefined;
        const g = c.getContext('2d');
        const mirno = smanjiPokret();
        const n = Math.round(a.gustina * (mirno ? 0.35 : 1));
        const boje = ['#f44336', '#ffb300', '#43a047', '#1e88e5', '#8e24aa', '#ff7043'];
        let w, h;
        const velicina = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
        velicina();
        window.addEventListener('resize', velicina);
        const novo = (pocetak) => ({
            x: Math.random() * w, y: pocetak ? Math.random() * h : -30, r: 4 + Math.random() * 6,
            vy: (a.vrsta === 'mehurici' ? -1 : 1) * (0.6 + Math.random() * 1.6) * (mirno ? 0.4 : 1),
            vx: (Math.random() - 0.5) * 0.8, ugao: Math.random() * 6.28, vu: (Math.random() - 0.5) * 0.1,
            boja: boje[Math.floor(Math.random() * boje.length)],
        });
        const cestice = Array.from({ length: n }, () => novo(true));
        if (a.vrsta === 'mehurici') cestice.forEach((p) => { p.y = h + Math.random() * h; });
        let id;
        const crtaj = () => {
            g.clearRect(0, 0, w, h);
            cestice.forEach((p, i) => {
                p.x += p.vx + (a.vrsta === 'sneg' ? Math.sin(p.ugao) * 0.4 : 0);
                p.y += p.vy; p.ugao += p.vu;
                if ((p.vy > 0 && p.y > h + 30) || (p.vy < 0 && p.y < -30)) { cestice[i] = novo(false); if (a.vrsta === 'mehurici') cestice[i].y = h + 30; }
                g.save();
                g.translate(p.x, p.y);
                if (a.vrsta === 'konfete') { g.rotate(p.ugao); g.fillStyle = p.boja; g.fillRect(-p.r, -p.r / 2, p.r * 2, p.r); }
                else if (a.vrsta === 'sneg') { g.fillStyle = 'rgba(255,255,255,.9)'; g.shadowColor = 'rgba(120,160,220,.6)'; g.shadowBlur = 4; g.beginPath(); g.arc(0, 0, p.r * 0.6, 0, 6.28); g.fill(); }
                else if (a.vrsta === 'mehurici') { g.strokeStyle = 'rgba(120,190,255,.7)'; g.lineWidth = 1.5; g.beginPath(); g.arc(0, 0, p.r * 1.4, 0, 6.28); g.stroke(); }
                else { g.rotate(p.ugao * 0.3); g.font = `${p.r * 3.2}px serif`; g.textAlign = 'center'; g.fillText(a.emodzi || '🎉', 0, 0); }
                g.restore();
            });
            id = requestAnimationFrame(crtaj);
        };
        id = requestAnimationFrame(crtaj);
        return () => { cancelAnimationFrame(id); window.removeEventListener('resize', velicina); };
    }, [a.vrsta, a.gustina, a.emodzi]);
    return <canvas ref={ref} className="zabava-platno" aria-hidden="true" />;
}

/** zvuk u pozadini: efekat na svakih N sekundi ili radio; počinje posle prve interakcije (pravilo pregledača) */
function Zvuk({ z }) {
    useEffect(() => {
        let audio = null, tajmer = null, pokrenuto = false;
        const pokreni = () => {
            if (pokrenuto) return;
            pokrenuto = true;
            if (z.vrsta === 'radio' && z.kanal) {
                audio = new Audio(z.kanal);
                audio.volume = Math.min(0.7, z.jacina / 100);
                audio.play().catch(() => { pokrenuto = false; });
            } else {
                zvukKontekst();
                pustiEfekat(z.efekat, z.jacina);
                tajmer = setInterval(() => pustiEfekat(z.efekat, z.jacina), z.interval * 1000);
            }
        };
        const prvi = () => pokreni();
        pokreni(); // ako je korisnik već kliknuo negde, kreće odmah
        document.addEventListener('pointerdown', prvi);
        document.addEventListener('keydown', prvi);
        return () => {
            document.removeEventListener('pointerdown', prvi);
            document.removeEventListener('keydown', prvi);
            if (tajmer) clearInterval(tajmer);
            if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
        };
    }, [z.vrsta, z.kanal, z.efekat, z.interval, z.jacina]);
    return null;
}

/** izgled cele aplikacije: tema, font, klackanje dugmadi — oznake na <html>, boje u index.css (zabava teme) */
function Izgled({ iz }) {
    useEffect(() => {
        const h = document.documentElement;
        if (iz.tema && iz.tema !== 'normalna') h.dataset.zabavaTema = iz.tema; else delete h.dataset.zabavaTema;
        if (iz.font === 'comic') h.dataset.zabavaFont = 'comic'; else delete h.dataset.zabavaFont;
        if (iz.klackanje) h.dataset.zabavaKlackanje = '1'; else delete h.dataset.zabavaKlackanje;
        return () => { delete h.dataset.zabavaTema; delete h.dataset.zabavaFont; delete h.dataset.zabavaKlackanje; };
    }, [iz.tema, iz.font, iz.klackanje]);
    return null;
}

/** trag emodžija iza miša */
function Kursor({ emodzi }) {
    useEffect(() => {
        if (smanjiPokret()) return undefined;
        let posl = 0;
        const pomeraj = (e) => {
            const t = performance.now();
            if (t - posl < 45) return;
            posl = t;
            const el = document.createElement('span');
            el.className = 'zabava-kursor';
            el.textContent = emodzi || '✨';
            el.style.left = e.clientX + 'px';
            el.style.top = e.clientY + 'px';
            document.body.appendChild(el);
            setTimeout(() => el.remove(), 900);
        };
        window.addEventListener('pointermove', pomeraj);
        return () => window.removeEventListener('pointermove', pomeraj);
    }, [emodzi]);
    return null;
}

/** poruka usred ekrana: zatvara se sa "U redu"; nova verzija poruke ponovo iskače */
const KLJUC_PORUKE = 'zabavaPoruka';
const procitanaVerzija = () => { try { return localStorage.getItem(KLJUC_PORUKE); } catch { return null; } };
function Poruka({ p }) {
    const [zatvorena, setZatvorena] = useState(procitanaVerzija() === String(p.verzija));
    useEffect(() => { setZatvorena(procitanaVerzija() === String(p.verzija)); }, [p.verzija]);
    if (zatvorena || !p.tekst) return null;
    const zatvori = () => { try { localStorage.setItem(KLJUC_PORUKE, String(p.verzija)); } catch { /* privatni režim */ } setZatvorena(true); };
    return (
        <Dialog header={p.naslov || 'Obaveštenje'} visible onHide={zatvori} modal closable={false} draggable={false} className="zabava-poruka"
            style={{ width: 'min(28rem, 92vw)' }} footer={<Button label="U redu" icon="pi pi-check" autoFocus onClick={zatvori} />}>
            <p>{p.tekst}</p>
        </Dialog>
    );
}

/** slika, animacija, zvuk, izgled, kursor i poruka (sve van zaglavlja) */
export function ZabavaEfekti() {
    const z = useZabava();
    if (!z.aktivno) return null;
    return (
        <>
            {z.izgled && <Izgled iz={z.izgled} />}
            {z.kursor && z.kursor.ukljuceno && <Kursor emodzi={z.kursor.emodzi} />}
            {z.poruka && z.poruka.ukljuceno && <Poruka p={z.poruka} />}
            {z.animacija && z.animacija.ukljuceno && <Animacija a={z.animacija} />}
            {z.slika && z.slika.ukljuceno && <LeteciSlika slika={z.slika} />}
            {z.zvuk && z.zvuk.ukljuceno && <Zvuk z={z.zvuk} />}
        </>
    );
}
